import { Link } from 'react-router-dom';
import { ArrowLeft, Play } from 'lucide-react';
import type { PortfolioMediaTile } from '../../lib/portfolio-tiles';
import { getMediaAlt } from '../../lib/portfolio-tiles';
import type { CategoryAccent } from '../../lib/category-theme';
import { getCategoryIcon } from '../../lib/category-theme';

type Props = {
  title: string;
  slug?: string;
  tiles: PortfolioMediaTile[];
  accent: CategoryAccent;
  imgSrc: (url?: string) => string;
  onOpenTile: (tile: PortfolioMediaTile) => void;
};

export default function PortfolioCategoryAlbum({
  title,
  slug,
  tiles,
  accent,
  imgSrc,
  onOpenTile,
}: Props) {
  const Icon = getCategoryIcon(slug, title);
  const photoCount = tiles.filter((t) => t.kind === 'photo').length;
  const videoCount = tiles.filter((t) => t.kind === 'video').length;

  return (
    <section className="pb-16 pt-20 md:pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Link
          to="/portfolio"
          className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-zinc-400 transition hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Toutes les catégories
        </Link>

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent.iconBg} text-white shadow-lg`}>
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <h1 className={`text-2xl font-black md:text-4xl ${accent.text}`}>{title}</h1>
              <p className="mt-1 text-sm text-zinc-400">
                {photoCount} photo{photoCount !== 1 ? 's' : ''}
                {videoCount > 0 && ` · ${videoCount} vidéo${videoCount !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{ columnCount: 4, columnGap: '4px' }}
          className="sm:[column-count:2] md:[column-count:3] lg:[column-count:4]"
        >
          {tiles.map((tile) => (
            <AlbumTile
              key={tile.id}
              tile={tile}
              imageUrl={imgSrc(tile.imageUrl)}
              accent={accent}
              onOpen={() => onOpenTile(tile)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AlbumTile({
  tile,
  imageUrl,
  accent,
  onOpen,
}: {
  tile: PortfolioMediaTile;
  imageUrl: string;
  accent: CategoryAccent;
  onOpen: () => void;
}) {
  const isVideo = tile.kind === 'video';
  const alt = getMediaAlt(tile);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative mb-1 block w-full overflow-hidden rounded-sm bg-zinc-900 text-left"
      style={{ breakInside: 'avoid' }}
    >
      <img
        src={imageUrl}
        alt={alt}
        draggable={false}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        className="block w-full object-cover transition duration-500 group-hover:scale-[1.02]"
        onContextMenu={(e) => e.preventDefault()}
      />

      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/25 transition group-hover:bg-black/40">
          <span className={`flex h-12 w-12 items-center justify-center rounded-full ${accent.iconBg} text-white shadow-lg`}>
            <Play className="ml-0.5 h-5 w-5 fill-white" />
          </span>
        </div>
      )}

      <div
        className="absolute inset-0"
        aria-hidden
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />

      <div className="pointer-events-none absolute bottom-2 right-2">
        <span className="rounded bg-black/40 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white/90">
          L Benna · Confidentiel
        </span>
      </div>
    </button>
  );
}
